import { Editor, IndexKey, TLShape, getIndexBetween } from "tldraw";

/**
 * Highest premade `zIndex` this editor has been asked to slot, so the common
 * ascending arrival can skip the sibling scan below.
 *
 * Seeded from a real scan on first use, not from 0 — a page restored from a
 * snapshot already carries premade shapes and must be measured before any skip.
 *
 * WeakMap so two canvases in one tab cannot share a mark, and so the entry dies
 * with the editor.
 *
 * A mark above the page's true max only costs extra scans. A mark below it
 * could skip a needed scan and misplace a shape, so the first use always measures.
 */
const highestPremadeZ = new WeakMap<Editor, number>();

/**
 * Compute the tldraw stacking index for a premade shape so that premade
 * shapes layer by their authored editor zIndex, regardless of the order
 * fragments get placed in (ENGG-2210).
 *
 * Only premade shapes participate: anything without a numeric meta.zIndex
 * (student ink, agent drawings) is invisible to this ordering and keeps
 * tldraw's default top-of-stack placement. Because every premade shape is
 * slotted on arrival, the invariant "premade zIndex is non-decreasing in
 * sibling order" holds by induction — no re-sorting pass exists or is needed.
 *
 * Returns undefined when the default top-of-stack placement is already
 * correct (no premade shape with a higher zIndex on the page yet).
 */
export function getPremadeStackingIndex(
  editor: Editor,
  zIndex: number,
): IndexKey | undefined {
  // Nothing can sit above this one, so the scan below would only confirm it.
  const known = highestPremadeZ.get(editor);
  if (known !== undefined && zIndex >= known) {
    highestPremadeZ.set(editor, zIndex);
    return undefined;
  }

  const pageId = editor.getCurrentPageId();
  const siblings = editor
    .getSortedChildIdsForParent(pageId)
    .map((id) => editor.getShape(id))
    .filter((s): s is TLShape => s !== undefined);

  // Position (in sibling order, back-to-front) of the topmost premade shape
  // with zIndex <= the new shape's, and of the lowest premade shape above it.
  let lastLowerOrEqual = -1;
  let firstHigher = -1;
  let pageMax = Number.NEGATIVE_INFINITY;
  siblings.forEach((sibling, i) => {
    const z = sibling.meta?.zIndex;
    if (typeof z !== "number") return;
    if (z > pageMax) pageMax = z;
    if (z <= zIndex) {
      lastLowerOrEqual = i;
    } else if (firstHigher === -1) {
      firstHigher = i;
    }
  });

  highestPremadeZ.set(editor, Math.max(pageMax, zIndex));

  // Nothing needs to sit above the new shape — default placement (top) is
  // correct, and it also covers legacy fragments with no zIndex at all.
  if (firstHigher === -1) return undefined;

  // Insert directly above the topmost premade shape at-or-below the new z,
  // so student/agent shapes drawn over that band stay on top of the new
  // arrival too. With no lower premade band, slot just under the lowest
  // higher one. Both bounds are adjacent siblings, so the minted key can
  // never collide with an existing shape's index.
  //
  // Min-clamp against firstHigher: if a student reordered premade shapes
  // with bring-forward/send-backward, the non-decreasing invariant may not
  // hold and lastLowerOrEqual can sit above firstHigher — inserting below
  // the lowest higher-z shape is then the least-wrong slot.
  const insertAt =
    lastLowerOrEqual >= 0
      ? Math.min(lastLowerOrEqual + 1, firstHigher)
      : firstHigher;
  return getIndexBetween(
    siblings[insertAt - 1]?.index,
    siblings[insertAt]?.index,
  );
}
